import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { format, addDays, startOfDay, endOfMonth } from 'date-fns';

const ALL_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00',
  '16:00', '17:00', '18:00', '19:00', '20:00',
];

function slotsFromWindow(openTime, closeTime) {
  if (!openTime || !closeTime) return ALL_SLOTS;
  const [oh] = openTime.split(':').map(Number);
  const [ch] = closeTime.split(':').map(Number);
  if (Number.isNaN(oh) || Number.isNaN(ch) || ch < oh) return ALL_SLOTS;
  const out = [];
  for (let h = oh; h <= ch; h++) out.push(`${String(h).padStart(2, '0')}:00`);
  return out.length ? out : ALL_SLOTS;
}

const LEAD_TIME_HOURS = 48;

/**
 * Fetches party-level availability: time slots where ALL standard (non-child)
 * rigs are simultaneously free. Calls the getPartyAvailability backend function
 * (service-role, bypasses Booking RLS) and computes per-slot + per-day
 * availability for the party calendar.
 *
 * A slot is party-available when zero bookings exist across any standard rig
 * for that date + slot. The 48-hour lead-time filter is applied here: any slot
 * whose datetime is less than 48 hours from now is not selectable.
 */
export function usePartyAvailability(visibleMonth) {
  const rangeStart = startOfDay(new Date());
  const defaultEnd = addDays(rangeStart, 90);
  const monthEnd = visibleMonth ? endOfMonth(visibleMonth) : null;
  const rangeEnd = (monthEnd && monthEnd > defaultEnd) ? monthEnd : defaultEnd;

  const { data = { standardRigs: [], bookings: [], experience_hours: null, bookings_paused: false } } = useQuery({
    queryKey: ['party-availability', format(rangeStart, 'yyyy-MM-dd'), format(rangeEnd, 'yyyy-MM-dd')],
    queryFn: async () => {
      const res = await base44.functions.invoke('getPartyAvailability', {
        startDate: format(rangeStart, 'yyyy-MM-dd'),
        endDate: format(rangeEnd, 'yyyy-MM-dd'),
      });
      return res.data;
    },
    staleTime: 1000 * 30,
  });

  const standardRigs = data.standardRigs || [];
  const bookings = data.bookings || [];
  const bookingsPaused = !!data.bookings_paused;
  const experienceHours = data.experience_hours || {};
  const slots = useMemo(
    () => slotsFromWindow(experienceHours.open_time, experienceHours.close_time),
    [experienceHours.open_time, experienceHours.close_time],
  );
  const openDays = useMemo(
    () => (Array.isArray(experienceHours.open_days) && experienceHours.open_days.length
      ? experienceHours.open_days
      : [0, 1, 2, 3, 4, 5, 6]),
    [experienceHours.open_days],
  );

  // date → slot → booking count (across all standard rigs)
  const byDateSlot = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.date || !b.time_slot) continue;
      if (!map[b.date]) map[b.date] = {};
      map[b.date][b.time_slot] = (map[b.date][b.time_slot] || 0) + 1;
    }
    return map;
  }, [bookings]);

  // A slot is party-available if zero bookings across any standard rig.
  // When bookings are paused, no slot is available.
  const isSlotAvailable = useCallback((dateStr, slot) => {
    if (bookingsPaused) return false;
    const count = byDateSlot[dateStr]?.[slot] || 0;
    return count === 0;
  }, [byDateSlot, bookingsPaused]);

  // 48-hour lead-time check: is this slot's datetime >= 48h from now?
  const isWithinLeadTime = useCallback((dateStr, slot) => {
    if (!dateStr || !slot) return true;
    const slotHour = parseInt(slot.split(':')[0], 10);
    const slotDate = new Date(dateStr + 'T00:00:00');
    slotDate.setHours(slotHour, 0, 0, 0);
    const cutoff = new Date(Date.now() + LEAD_TIME_HOURS * 60 * 60 * 1000);
    return slotDate < cutoff;
  }, []);

  // Per-slot availability for a date (for the time grid).
  // Returns { [slot]: { available, withinLeadTime } }
  const slotAvailability = useCallback((dateStr) => {
    const result = {};
    const now = new Date();
    const todayStr = format(now, 'yyyy-MM-dd');
    for (const slot of slots) {
      const past = dateStr === todayStr && parseInt(slot.split(':')[0], 10) <= now.getHours();
      const withinLead = isWithinLeadTime(dateStr, slot);
      const avail = !past && !withinLead && isSlotAvailable(dateStr, slot);
      result[slot] = { available: avail, withinLeadTime: withinLead, past };
    }
    return result;
  }, [slots, isSlotAvailable, isWithinLeadTime]);

  // Day hint for the calendar: 'open' | 'limited' | 'full' | 'closed'.
  const dayHint = useCallback((dateStr) => {
    const dow = new Date(dateStr + 'T00:00:00').getDay();
    if (!openDays.includes(dow)) return 'closed';
    if (bookingsPaused) return 'full';
    const slotMap = byDateSlot[dateStr];
    if (!slotMap) return 'open';
    // Count how many slots have any booking
    let blockedSlots = 0;
    for (const slot of slots) {
      if ((slotMap[slot] || 0) > 0) blockedSlots++;
    }
    const totalSlots = slots.length;
    if (blockedSlots === totalSlots) return 'full';
    if (blockedSlots > 0) return 'limited';
    return 'open';
  }, [byDateSlot, slots, openDays, bookingsPaused]);

  // Does this date have ANY party-available slot outside the 48h window?
  const hasAvailableSlot = useCallback((dateStr) => {
    const sa = slotAvailability(dateStr);
    return slots.some((s) => sa[s].available);
  }, [slotAvailability, slots]);

  // Is the entire date within the 48h lead-time window?
  const isDateWithinLeadTime = useCallback((dateStr) => {
    return slots.every((s) => isWithinLeadTime(dateStr, s));
  }, [slots, isWithinLeadTime]);

  return {
    standardRigs,
    bookings,
    bookingsPaused,
    slots,
    openDays,
    isSlotAvailable,
    isWithinLeadTime,
    slotAvailability,
    dayHint,
    hasAvailableSlot,
    isDateWithinLeadTime,
  };
}